import ApiService from './api';

type RagChannelId = string | null;
type RagIntent = {
  intent: string;
  stagingChannels?: RagChannelId[];
  liveChannels?: RagChannelId[];
  [key: string]: any;
};
type RAGStatus = 'draft' | 'active' | 'active_failed_update' | 'failed' | 'queued' | 'uploading' | 'scraping' | 'indexing' | 'failed_scrape' | 'failed_indexing' | 'failed_quota_reached' | 'failed_too_large' | 'deleted' | string;
type RAGResourceOptions = Record<string, any>;
type RAGResourceUpdate = Record<string, any>;
type RAGResourceOverview = {
  _id: string;
  resourceId?: string;
  ressourceId?: string;
  [key: string]: any;
};
type RAGResource = Record<string, any>;
type RAGQueryOptions = {
  topK?: number;
  filter?: Record<string, string>;
  [key: string]: any;
};
type GetAnswerStatsInfo = {
  source?: string;
  count?: number;
};
type RAGAnswer = Record<string, any>;
type RAGQueryResponse = { status: 'ok' | 'error' | string; [key: string]: any };
type RAGDeleteResponse = RAGQueryResponse & { result?: { status: string; num_docs_deleted: number } };
type RAGActionPayload = { id?: string; intent: string; [key: string]: any };
type RAGFollowupActionPayload = { channelId: string; [key: string]: any };
type RAGOutputGatePayload = { channelId: string; output_gate: Record<string, any> | null; [key: string]: any };
/**
 * How the buttons of an action-driven card are produced.
 * 'static' (default) repeats the card's own buttons on every card, each field rendered as a Handlebars
 * template against the item. 'dynamic' renders one button per entry of the array at `source`, using the
 * FIRST button of the card as the template; any further buttons on the card are ignored while in this mode
 * (they are kept, so switching back to 'static' restores them).
 */
type AnswerTemplateButtonBinding = {
  /** 'static' (default) repeats the card's own buttons; 'dynamic' renders one button per array entry. */
  mode?: 'static' | 'dynamic';
  /** Dot-path to the button array, resolved INSIDE the current item. Empty means the item itself is the array. */
  source?: string;
  /** Reserved: optional cap on the number of rendered buttons. Not exposed in the UI yet. */
  limit?: number;
};
/** Binding to an AI Action whose response feeds the cards (fillMode 'action'). */
type AnswerTemplateActionBinding = {
  /** Id of the AI Action whose response feeds the cards (fillMode 'action'). */
  actionId?: string;
  /** Dot-path to the array inside the action's response JSON. Empty means the response itself is the array. */
  source?: string;
  /** Optional cap on the number of rendered slides. */
  limit?: number;
  /** How the card's buttons are produced. Absent defaults to 'static' mode. */
  buttons?: AnswerTemplateButtonBinding;
};
type AnswerTemplateAnswer = {
  text?: string;
  buttons?: any[] | null;
  cards?: any[] | null;
  /**
   * Carried over from the answer template that produced this answer, so renderers (widget, playground)
   * fit the card images the same way. Defaults to 'cover' when absent.
   */
  imageDisplayMode?: 'cover' | 'contain';
  /**
   * Where a card's image comes from: 'llm' (default) uses the card's image_url field, 'og'
   * resolves it server-side from the Open Graph metadata of the card's target URL (its first button).
   */
  imageSource?: 'llm' | 'og';
  [key: string]: any;
};
type AnswerTemplateFullDocument = {
  _id?: string;
  botId: string;
  name: string;
  instruction: string;
  answer: AnswerTemplateAnswer;
  /** How card contents are produced: 'llm' (default) lets the LLM fill the template, 'action' renders cards server-side from the bound AI Action's response. */
  fillMode?: 'llm' | 'action';
  /** How the widget fits card images: 'cover' (default) fills and crops, 'contain' shows the full image letterboxed. */
  imageDisplayMode?: 'cover' | 'contain';
  /** Where a card's image comes from: 'llm' (default) uses the card's image_url field, 'og' resolves it server-side from the Open Graph metadata of the card's target URL (its first button). */
  imageSource?: 'llm' | 'og';
  /** Binding to an AI Action, used when fillMode is 'action'. */
  actionBinding?: AnswerTemplateActionBinding;
  [key: string]: any;
};
type LoadRagResourcesFilters = {
  resourceIds?: string[];
  statuses?: string[];
  channelIds?: string[];
};
type LoadRagResourcesOptions = {
  withConnections?: boolean;
  withContent?: boolean;
  withDeleted?: boolean;
};
type RagResourcePayload = Record<string, any>;
type RagSearchOptions = {
  except?: string | null;
  topK?: number | null;
};
type ResourceAbstractParams = {
  botId: string;
  resourceDocId: string;
  title?: string;
  abstractText?: string;
  regenerate?: boolean;
  embeddingName?: string;
  protectedAbstract?: boolean;
};
type AxiosErrorLike = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const rag = (botId: string, path: string, v2 = false) => `/schaltzentrale/rag${v2 ? '/v2/' : '/'}bot/${botId}${path}`;

const RAGService = {
  /**
   * Creates a new resource
   * @template {RAGResourceDraftDocument | RagPDF | RagWEBPAGE | RagMARKDOWN | RagQNA} ResourcePayload
   * @param {string} botId
   * @param {string} channelId
   * @param {ResourcePayload} resource
   * @param {RagIntent[]} connectToIntents
   * @throws {Error} If it fails to call the API
   * @returns {Promise<{resource: Object, intents: Object[], message?: string, status: 'ok'|'error'}>}
   */
  async addNewResource(
    botId: string,
    channelId: string,
    resource: RagResourcePayload,
    connectToIntents: RagIntent[]
  ) {
    /**
     * `isNormalizedConnections` tells API that we are sending {@link RagIntent} instead of something like {intent: string, box: string}[].
     * This was the old way of doing it, and should be deprecated at some point.
     */
    const r = await ApiService.post(
      `/schaltzentrale/rag/v3/bot/${botId}/resources?isNormalizedConnections=true`,
      {
        resource,
        channelId,
        connectToIntents,
      }
    );

    return (
      r.data ?? {
        resource: null,
        intents: [],
        message: 'Something here',
      }
    );
  },
  /**
   * Updates a RAG Markdown resource with the new state.
   * Does a shallow merge of the new state onto the old.
   * @template {RAGResourceDraftDocument | RagPDF | RagWEBPAGE | RagMARKDOWN | RagQNA} ResourcePayload
   * @param {string} botId
   * @param {string} docId The resource Document ID (_id)
   * @param {Partial<ResourcePayload>} resource Whole document or parts to update
   * @throws {Error} If it fails to call the API
   * @returns {Promise<null | Partial<RemoteRAGResource<ResourcePayload>>>}
   */
  async updateResource(botId: string, docId: string, resource: Partial<RagResourcePayload>) {
    const r = await ApiService.put(rag(botId, `/resources/update/${docId}`, true), resource);
    return r.data ?? null;
  },
  /**
   * Update meta-properties of a {@link RAGResource}, such as scraping options and update schedule.
   * Will not touch the RAG Index or the Rag Resource Content in any way.
   * Performs a shallow merge in the backend.
   * @param {string} botId
   * @param {string} docId The resource Document ID (_id)
   * @param {Partial<{options: RAGResourceOptions, update: RAGResourceUpdate}>} properties The properties you want to mutate
   * @throws {Error} If it fails to call the API
   * @returns {Promise<{success: boolean, resource: {options: RAGResourceOptions, update: RAGResourceUpdate}}>}
   * The `resource` property contains the **full new** states of the properties you are allowed to update.
   */
  async updateResourceProperties(
    botId: string,
    docId: string,
    properties: Partial<{ options: RAGResourceOptions; update: RAGResourceUpdate }>
  ) {
    const r = await ApiService.put(rag(botId, `/resources/${docId}`, true), properties);
    return r.data ?? null;
  },
  /**
   * @param {string} botId
   * @param {string} intent
   * @returns {Promise<RAGResourceOverview[]>}
   */
  getByIntent: async (botId: string, intent: string) => {
    try {
      const r = await ApiService.get(rag(botId, `/resources?intent=${intent}`, true));
      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * @param {string} botId
   * @param {string} resourceId rag ressourceId
   * @returns {Promise<RAGResource | null>}
   */
  getByResourceID: async (botId: string, resourceId: string) => {
    try {
      const result = await ApiService.get(
        rag(botId, `/resources/${resourceId}`, true)
      );

      return result.data?.result ?? null;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * @param {string} botId
   * @param {string} resourceDocId rag ressourceId
   * @returns {Promise<RAGResource | null>}
   */
  getByResourceDocID: async (botId: string, resourceDocId: string) => {
    try {
      const result = await ApiService.get(
        rag(botId, `/resources/id/${resourceDocId}`, true)
      );

      return result.data?.result ?? null;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * @param {string} botId
   * @param {string} intent
   * @returns {Promise<void>}
   */
  deleteByIntent: async (botId: string, intent: string) => {
    throw new Error('To be implemented'); // No idea what the response is like here
    try {
      return await ApiService.delete(rag(botId, `/intent/${intent}`));
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * @param {string} botId
   * @param {string} id MongoDB ID of the resource
   * @returns {Promise<RAGDeleteResponse>}
   * @throws {Error} Usually if it does not exist, or there are intents that still use this resource
   */
  deleteByID: async (botId: string, id: string): Promise<RAGDeleteResponse> => {
    const r = await ApiService.delete(rag(botId, `/resources/resourceDocId/${id}`, true));
    return r.data;
  },
  /**
   * @param {string} botId
   * @param {?{intent?: string, channel?: string, status?: RAGStatus, environment?: "live"|"staging"}} [query={}]
   * @returns {Promise<RAGResourceOverview[]>}
   * @deprecated - Use the new `getResources` instead
   */
  getAllByBot: async (
    botId: string,
    query: { intent?: string; channel?: string; status?: RAGStatus; environment?: 'live' | 'staging' } = {}
  ) => {
    try {
      const qs = new URLSearchParams(query);
      const r = await ApiService.get(rag(botId, `/resources?${qs.toString()}`, true));

      // Not sure how the error would look like
      if (r.status !== 200) {
        throw new Error(
          r.data?.error ||
          r.data?.message ||
          r.data?.result ||
          'Unknown error fetching resources'
        );
      }

      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * @param {string} botId
   * @returns {Promise<void>}
   */
  deleteAllByBot: async (botId: string) => {
    throw new Error('To be implemented'); // No idea what the response is like here
    try {
      return await ApiService.delete(rag(botId, `/ressources`));
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * @param {string} botId
   * @param {string} intent
   * @param {string} query
   * @param {?RAGQueryOptions} [options={}]
   * @returns {Promise<void>}
   */
  getSimilarDocs: async (botId: string, intent: string, query: string, options: RAGQueryOptions = {}) => {
    throw new Error('To be implemented'); // No idea what the response is like here
    try {
      return await ApiService.post(rag(botId, `/simdocs`), {
        intent,
        query,
        ...options,
      });
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * Checks what the status of a document is.
   * Since the resource might not be ready, they will not have a resourceId, so we have to use URL
   * @param {string} botId
   * @param {string} intent
   * @param {string} resourceUrl
   * @returns {Promise<null | Omit<RAGStatus, 'draft'>>}
   */
  async checkResourceStatus(botId: string, intent: string, resourceUrl: string) {
    try {
      const r = await ApiService.get(
        rag(botId, `/intent/${intent}/status?url=${resourceUrl}`)
      );
      return r.data?.result ?? null;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * @typedef {Object} GetAnswerStatsInfo
   * @prop {string} source Arbitrary identifier what feature/UI/location wanted to have an answer generated
   * @prop {number} [count=0] Arbitrary counter. What the count means depends on the source
   */
  /**
   * @param {string} botId
   * @param {string} intent
   * @param {string} query
   * @param {?RAGQueryOptions & GetAnswerStatsInfo} [options={}]
   * @param {?string} [channelId=null]
   * @param {?object} [dialog=null]
   * @returns {Promise<RAGQueryResponse & {result: RAGAnswer}>}
   */
  getAnswer: async (
    botId: string,
    intent: string,
    query: string,
    options: RAGQueryOptions & GetAnswerStatsInfo = {},
    channelId: string | null = null,
    dialog: object | null = null
  ): Promise<(RAGQueryResponse & { result: RAGAnswer }) | null> => {
    try {
      let url = rag(botId, `/intent/${intent}/answer`);

      if (channelId) {
        url = rag(botId, `/intent/${intent}/channel/${channelId}/answer`);
      }

      // This is for stats
      const q = new URLSearchParams();
      const queryOptions = { ...options };
      if (queryOptions.source) {
        q.append('source', queryOptions.source);
        delete queryOptions.source;
      }
      if (queryOptions.count !== undefined) {
        q.append('count', String(queryOptions.count));
        delete queryOptions.count;
      }
      const queryString = q.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const r = await ApiService.post(url, {
        query,
        ...queryOptions,
        ...(dialog ? { dialog } : {}),
      });

      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
	/**
	 * Retrieves questions that could not be answered by this RAG intent due to insufficient knowledge
	 * @param {string} botId
	 * @param {string} intent
	 * @param {?string | null} [channelId = null] Channel to use
	 * @param {?number} [page=1]
	 * @param {?number} [pageSize=50]
	 * @returns {Promise<null | { question: string, created: string }[]>} `null` if an error occurred
	 * @deprecated - Use rag_v2.js
	 */
  async getMissingRAGKnowledge(
    botId: string,
    intent: string,
    channelId: string | null = null,
    page = 1,
    pageSize = 50
  ) {
		try {
			let url = rag(botId, `/intent/${intent}/missing-knowledge`);
			if (channelId) {
				url = rag(botId, `/intent/${intent}/missing-knowledge?channelId=${channelId}`);
				url += `&page=${page}&pageSize=${pageSize}`;
			} else {
				url += `?page=${page}&pageSize=${pageSize}`;
			}

			const r = await ApiService.get(url);
			if (r.data?.data && r.data?.data.length > 0) {
				return r.data;
			}

			return {page: 1, data: r.data, total: r.data.length, pageSize: 50};
		} catch(e) {
			console.error(e);
			return null;
		}
	},
  /**
   * Overwrites which intents are assigned to this resource, and in which stage per-channel they are.
   * Any omitted channels/stages/intents will be "unassigned".
   * @param {string} botId
   * @param {RAGResourceOverview["resourceId"]} resourceId
   * @param {RagIntent[]} intentObjects
   * @returns {Promise<{updatedIntents: string[], resourceIntents: RagIntent[]}>}
   * @todo Return should be the new state, and we should set our current state to its response, to ensure it is 1:1 correct!
   */
  async setAssignedIntents(botId: string, resourceId: RAGResourceOverview['resourceId'], intentObjects: RagIntent[]) {
    // Don't mutate reference - it requires "null" to be `null`
    const prepared = structuredClone(intentObjects).map((intent: RagIntent) => {
      if (intent?.stagingChannels?.length) intent.stagingChannels = intent.stagingChannels.map((c) => c === 'null' ? null : c);
      if (intent?.liveChannels?.length) intent.liveChannels = intent.liveChannels.map((c) => c === 'null' ? null : c);

      return intent;
    });

    try {
      const r = await ApiService.post(
        rag(botId, `/resources/${resourceId}/intents`, true),
        prepared
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * Overwrite which resources are attached to a specific intent
   * @param {string} botId
   * @param {string} resourceId
   * @param {string} intentName
   * @param {string|null} channelId
   * @param {'staging'|'live'} environment Whether to apply the change to live or staging
   * @param {boolean} shouldBeConnected
   * @returns {Promise<{resources: {staging?: Record<string, string[]>, live?: Record<string, string[]>}, resource: object}>}
   */
  async setResourceAssignment(
    botId: string,
    resourceId: string,
    intentName: string,
    channelId: string | null,
    environment: 'staging' | 'live',
    shouldBeConnected: boolean
  ) {
    const r = await ApiService.put(
      `/schaltzentrale/rag/v3/bot/${botId}/resources/${resourceId}/set-connection`,
      {
        channelId: channelId + '',
        connected: !!shouldBeConnected,
        environment,
        intentName,
      }
    );

    return r.data;
  },
  /**
   * Sets whether the default intent is enabled or not
   * @param {string} botId
   * @param {boolean} newState
   * @returns {Promise<null | {success: boolean, fallbackFaqIntent: Object}>}
   */
  async setDefaultEnabled(botId: string, newState: boolean) {
    try {
      const r = await ApiService.post(
        rag(botId, `/intent/fallbackFaq`),
        { active: newState },
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },

  /**
   * @typedef {Object} GetRagResourcesCharacterCountResult
   * @property {'ok'|'error'} status The status of the request
   * @property {number} total The total character count of all resources that had a character count set
   * @property {Array<{_id: string, ressourceId?: string}>} unknownCounts Records that did not have a character count set.
   * The `ressourceId` property will only be included if the query had `options.includeRessourceId` set to true.
   * @property {?string} [message] A display error message if status is "error"
   * @property {?number} [code] A technical error code if status is "error"
   */
  /**
   * Retrieve the character counts for resources.
   * Use-cases
   * - total character count used for a bot
   * - character count for a specific resource
   * - character count for a specific set of resources
   * @param {string} botId The ID of the bot. Usually you want to use the Live bot ID.
   * @param {Object} [options={}] Additional options/filters to reduce resources further
   * @param {string[]} [options.ressourceIds] Only count from specific resources by their RessourceID (not Object ID)
   * @param {string[]} [options.ids] Only count from specific resources by their document ID (Object ID, not RessourceId).
   * @param {boolean} [options.includeRessourceId=false] If true, we will also include the `ressourceId` for resources that has unknown counts. This slows down the query a bit, so only enable if you need it.
   * @returns {Promise<GetRagResourcesCharacterCountResult>}
   * @example
   * const botId = "123123";
   * const ressourceIds = ["asd_452133_asd3dafaf3fa3fa3f", "qwert_wccas_f337agub73dg8373"];
   * const objectIds = [ObjectId("ffe438268eeab23234124342"), ObjectId("261398746987abcdef232445")];
   *
   * const all = await getResourcesCharacterCount(botId);
   * // ╙→ {status: 'ok', total: 5354623, unknownCounts: [{_id: ObjectId("abcdef123456789abcdef123")}] }
   * const allExtended = await getResourcesCharacterCount(botId, { includeRessourceId: true });
   * // ╙→ {status: 'ok', total: 5354623, unknownCounts: [{_id: ObjectId("abcdef123456789abcdef123"), ressourceId: "asd_452133_asd3dafaf3fa3fa3f"}] }
   * const specificA = await getResourcesCharacterCount(botId, { ressourceIds });
   * // ╙→ {status: 'ok', total: 12345, unknownCounts: [] }
   * const specificB = await getResourcesCharacterCount(botId, { ids: objectIds });
   * // ╙→ {status: 'ok', total: 63245, unknownCounts: [] }
   */
  async getRagResourcesCharacterCount(
    botId: string,
    options: { ressourceIds?: string[]; ids?: string[]; includeRessourceId?: boolean } = {}
  ) {
    try {
      /**
       * Prepare any query statements.
       * We can let Express deal with making arrays by defining multiple KVs with the same key.
       * `array=itemA&array=itemB&array=itemC` becomes `['itemA', 'itemB', 'itemC']`
       */
      let query = '?';

      if (options.ressourceIds?.length) {
        for (const id of options.ressourceIds) query += `ressourceIds=${id}&`;
      }

      if (options.ids?.length) {
        for (const id of options.ids) query += `ids=${id}&`;
      }

      if (options.includeRessourceId) query += `includeRessourceId=true&`;

      if (query !== '?') query = query.slice(0, -1); // Remove last '&'
      else query = ''; // Remove the '?' if no query was added

      const path = rag(botId, `/resources/counts`, true);
      const r = await ApiService.get(`${path}${query}`);
      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },

  /**
   * Fetches all actions/tools available to the LLM when generating answers
   * @param {string} botId
   * @returns {Promise<any[]>}
   */
  async fetchActions(botId: string) {
    try {
      //const qs = new URLSearchParams(query);
      const r = await ApiService.get('');

      // Not sure how the error would look like
      if (r.status !== 200) {
        throw new Error(
          r.data?.error ||
          r.data?.message ||
          r.data?.result ||
          'Unknown error fetching resources'
        );
      }

      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },

  /**
   * Create a new action for the bot's RAG system.
   * @param {string} botId The ID of the bot from the environment you want to target
   * @param {RAGActionPayload} payload
   * @returns {Promise<{error: true, message: string} | {status: 'ok', ragActions: IntentRagActions}>} Returns the new complete state of the RagActions for the deployed agent
   */
  async addNewAction(botId: string, payload: RAGActionPayload) {
    try {
      const r = await ApiService.put(
        `/schaltzentrale/rag/v3/bot/${botId}/intent/${payload.intent}/action`,
        payload,
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to create a new action.',
      };
    }
  },

  /**
   * Create a new action for the bot's RAG system.
   * @param {string} stagingBotId The ID of the bot from the environment you want to target (usually always staging)
   * @param {Partial<RAGActionPayload>} payload The new state(s) to update. Replaces each defined, and `parameters` array is replaced entirely
   * @returns {Promise<{error: true, message: string} | {status: 'ok', ragActions: IntentRagActions}>} Returns the new complete state of the RagActions for the agent
   */
  async updateAction(stagingBotId: string, payload: Partial<RAGActionPayload>) {
    if (!payload.id) return {
      error: true,
      message: 'Only existing actions can be updated, and they should have an ID field present',
    };

    try {
      const r = await ApiService.post(
        `/schaltzentrale/rag/v3/bot/${stagingBotId}/intent/${payload.intent}/action/update`,
        payload,
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to update the action.',
      };
    }
  },

  /**
   * Delete an action from the bot's RAG system
   * @param {string} botId The ID of the bot from the environment you want to target
   * @param {string} intent The technical name of the intent
   * @param {string} actionId
   * @param {string} channelId
   * @returns {Promise<{error: true, message: string} | {status: 'ok', ragActions: IntentRagActions}>}  Returns the new complete state of the RagActions for the deployed agent
   */
  async deleteAction(botId: string, intent: string, actionId: string, channelId: string) {
    try {
      const r = await ApiService.delete(`/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/action?id=${actionId}&channelId=${channelId}`);
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to delete the action.',
      };
    }
  },

  /**
   * Search for resources that could answer a given query.
   * Optionally, exclude a specific intent from the results.
   *
   * If you specify the `except`, it will not populate intents connected to each resource; the array will be empty.
   * @param {string} botId
   * @param {string} query The user inquiry
   * @param {Object} [options={}]
   * @param {?string} [options.except] Technical name of intent to exclude from the result
   * @param {?number} [options.topK] Pick top N matches. Default: 15 in the API
   * @returns {Promise<RagSearchResult>}
   */
  async ragSearch(botId: string, query: string, options: RagSearchOptions = {}) {
    try {
      const r = await ApiService.post(
        `/schaltzentrale/rag-search/bot/${botId}`,
        { query, ...options }
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return { status: 'error' };
    }
  },

  /**
   * Deploy the staging action state of an agent/intent to the live environment.
   * @param {string} botId The live bot ID
   * @param {string} intent The technical name of the intent
   * @param {string} channelId The channel ID to deploy the action to. Not `null` for default.
   * @returns {Promise<{error: true, message: string} | {status: 'ok', ragActions: IntentRagActions}>} Returns the new complete state of the RagActions for the deployed agent
   */
  async deployAgentActions(botId: string, intent: string, channelId: string) {
    try {
      const r = await ApiService.post(
        `/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/action/deploy`,
        { channelId },
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to deploy agent actions.',
      };
    }
  },

  /**
   * Deploy the staging resource state of an agent/intent to the live environment.
   * @param {string} botId The live bot ID
   * @param {string} intent The technical name of the intent
   * @param {string} channelId The channel ID to deploy the resource to. Not `null` for default.
   * @returns {Promise<{error: true, message: string} | {status: 'ok', resources: { live: any, staging: any }}>} Returns the new complete state of the resources for the deployed agent, separated into live and staging environments.

   */
  async deployAgentResources(botId: string, intent: string, channelId: string) {
    try {
      const r = await ApiService.post(
        `/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/resources/deploy`,
        { channelId },
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to deploy agent resources.',
      };
    }
  },

  /**
   * Deploy the staging followup action state of an agent/intent to the live environment.
   * @param {string} botId The live bot ID
   * @param {string} intent The technical name of the intent
   * @param {string} channelId The channel ID to deploy the action to. Not `null` for default.
   * @returns {Promise<{error: true, message: string} | {status: 'ok', followup: RAGFollowupAction}>} Returns the new complete state of the RagActions for the deployed agent
   */
  async deployAgentFollowupActions(botId: string, intent: string, channelId: string) {
    try {
      const r = await ApiService.post(
        `/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/followup/deploy`,
        { channelId }
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to deploy agent actions.',
      };
    }
  },

  /**
   * Create a new followup action for the bot's RAG system.
   * @param {string} botId The ID of the bot from the environment you want to target
   * @param {string} intent The technical name of the intent
   * @param {RAGFollowupActionPayload} payload
   * @returns {Promise<{error: true, message: string} | {status: 'ok', followup: RAGFollowupAction}>} Returns the created followup action
   */
  async addNewFollowupAction(botId: string, intent: string, payload: RAGFollowupActionPayload) {
    try {
      const r = await ApiService.put(
        `/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/followup`,
        payload,
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to create a new followup action.',
      };
    }
  },

  /**
   * Get an existing followup action for the bot's RAG system.
   * @param {string} botId The ID of the bot from the environment you want to target
   * @param {string} intent The technical name of the intent
   * @param {string} channelId The channel ID to get the followup action for
   * @returns {Promise<{error: true, message: string} | {status: 'ok', followupAction: RAGFollowupAction}>} Returns the followup action if it exists
   */
  async getFollowupAction(botId: string, intent: string, channelId: string) {
    try {
      const r = await ApiService.get(`/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/followup?channelId=${channelId}`);
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to retrieve the followup action.',
      };
    }
  },

  /**
   * Update an existing followup action for the bot's RAG system.
   * @param {string} botId The ID of the bot from the environment you want to target
   * @param {string} intent The technical name of the intent
   * @param {RAGFollowupActionPayload} payload
   * @returns {Promise<{error: true, message: string} | {status: 'ok', followupAction: RAGFollowupAction}>} Returns the updated followup action
   */
  async updateFollowupAction(botId: string, intent: string, payload: RAGFollowupActionPayload) {
    try {
      const r = await ApiService.post(
        `/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/followup/update`,
        payload,
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to update the followup action.',
      };
    }
  },

  /**
   * Delete an existing followup action from the bot's RAG system
   * @param {string} botId The ID of the bot from the environment you want to target
   * @param {string} intent The technical name of the intent
   * @param {string} channelId The channel ID to delete the followup action from
   * @returns {Promise<{error: true, message: string} | {status: 'ok'}>} Returns status indicating success
   */
  async deleteFollowupAction(botId: string, intent: string, channelId: string) {
    try {
      const r = await ApiService.delete(`/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/followup?channelId=${channelId}`);
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to delete the followup action.',
      };
    }
  },
  /**
   * Get the current output gate configuration for an agent/intent channel.
   * @param {string} botId The ID of the bot from the environment you want to target
   * @param {string} intent The technical name of the intent
   * @param {string} channelId The channel ID to get the output gate for
   * @returns {Promise<{error: true, message: string} | {status: 'ok', stageName: string, channelId: string, payload: {output_gate: RAGOutputGate}}>}
   */
  async getOutputGate(botId: string, intent: string, channelId: string) {
    try {
      const r = await ApiService.get(
        `/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/output-gate?channelId=${channelId}`
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to retrieve the output gate.',
      };
    }
  },

  /**
   * Update the output gate configuration for an agent/intent channel.
   * Pass `null` for a field to remove it; pass `{ output_gate: null }` to remove the whole gate.
   * @param {string} botId The ID of the bot from the environment you want to target
   * @param {string} intent The technical name of the intent
   * @param {RAGOutputGatePayload} payload Must include `channelId` and `output_gate`
   * @returns {Promise<{error: true, message: string} | {status: 'ok', payload: {output_gate: RAGOutputGate}}>}
   */
  async updateOutputGate(botId: string, intent: string, payload: RAGOutputGatePayload) {
    try {
      const r = await ApiService.put(
        `/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/output-gate`,
        payload,
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to update the output gate.',
      };
    }
  },

  /**
   * Deploy the staging output gate configuration to the live environment.
   * @param {string} botId The live bot ID
   * @param {string} intent The technical name of the intent
   * @param {string} channelId The channel ID to deploy the output gate for
   * @returns {Promise<{error: true, message: string} | {status: 'ok', channelId: string, staging: {output_gate: RAGOutputGate}, live: {output_gate: RAGOutputGate}}>}
   */
  async deployOutputGate(botId: string, intent: string, channelId: string) {
    try {
      const r = await ApiService.post(
        `/schaltzentrale/rag/v3/bot/${botId}/intent/${intent}/output-gate/deploy`,
        { channelId }
      );
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        error: true,
        message: 'An unknown error occurred while trying to deploy the output gate.',
      };
    }
  },

  /**
   * Retrieves all the Answer Templates for a given bot, channel ID, and agent (intent)
   * @param {string} botId Target bot ID. Can be the live or staging bot ID, depending on which you want.
   * Provide only `botId` to get all templates for this bot, regardless of channel/intent.
   * @param {string[]} [channelIds=[]] Note: Does NOT use `null` for the default channel.
   * Limit returned templates to those that are used in the given channel.
   * If omitted, it will not filter based on this.
   * @param {string[]} [intents=[]] The intent technical names.
   * Limit returned templates to those that are used in the given intent.
   * If omitted, it will not filter based on this.
   * @returns {Promise<{success: false; message: string} | {success: true; templates: AnswerTemplateFullDocument[]}>} If `success: false`, a `message` contains the error and `template` is not defined.
   * If success, `message` is not set and `templates` contains all matching templates found.
   */
  async getAnswerTemplates(botId: string, channelIds: string[] = [], intents: string[] = []) {
    const query = new URLSearchParams({});

    for (const id of channelIds) query.append(`channelIds`, id);
    for (const intent of intents) query.append(`intents`, intent);

    try {
      const r = await ApiService.get(`/schaltzentrale/rag/v3/bot/${botId}/templates?${query.toString()}`);

      if (r.data.message) {
        return {
          success: false,
          message: r.data.message,
        };
      }

      return {
        success: true,
        templates: r.data,
      };
    } catch (e) {
      console.error(e);
      return {
        success: false,
        message: 'An unknown error occurred while trying to retrieve answer templates.',
      };
    }
  },
  /**
   * Create a new template for an agent in your bot + channel
   * @param {Partial<AnswerTemplateFullDocument>} newTemplate The new template to be created. You don't need `_id` and date fields.
   * @returns {Promise<{success: false; message: string} | {success: true; template: AnswerTemplateFullDocument}>} If `success: false`, a `message` contains the error and `template` is not defined.
   * If success, `message` is not set and `template` contains the full state of your new template.
   */
  async createAnswerTemplate(newTemplate: Partial<AnswerTemplateFullDocument> & { botId: string }) {
    try {
      const r = await ApiService.post(
        `/schaltzentrale/rag/v3/bot/${newTemplate.botId}/templates`,
        newTemplate,
      );

      if (r.data.message) {
        return {
          success: false,
          message: r.data.message,
        };
      }

      return {
        success: true,
        template: r.data,
      };
    } catch (e) {
      console.error(e);
      return {
        success: false,
        message: 'An unknown error occurred while trying to create the answer template.',
      };
    }
  },
  /**
   * Updates an existing answer template to a new state.
   * Only certain fields can be changed
   * @param {AnswerTemplateFullDocument} newTemplateState The full template, in its new state
   * @returns {Promise<{success: false; message: string} | {success: true; template: AnswerTemplateFullDocument}>} If `success: false`, a `message` contains the error and `template` is not defined.
   * If success, `message` is not set and `template` contains the full new state of the template.
   */
  async updateAnswerTemplate(newTemplateState: AnswerTemplateFullDocument & { _id: string }) {
    try {
      const body: Record<string, any> = {
        name: newTemplateState.name,
        instruction: newTemplateState.instruction,
        answer: newTemplateState.answer,
      };
      // Card display / fill settings — only pass through when present
      if (newTemplateState.imageDisplayMode !== undefined) body.imageDisplayMode = newTemplateState.imageDisplayMode;
      if (newTemplateState.imageSource !== undefined) body.imageSource = newTemplateState.imageSource;
      if (newTemplateState.fillMode !== undefined) body.fillMode = newTemplateState.fillMode;
      if (newTemplateState.actionBinding !== undefined) body.actionBinding = newTemplateState.actionBinding;

      const r = await ApiService.put(
        `/schaltzentrale/rag/v3/bot/${newTemplateState.botId}/templates/${newTemplateState._id}`,
        body,
      );

      if (r.data.message) {
        return {
          success: false,
          message: r.data.message,
        };
      }

      return {
        success: true,
        template: r.data,
      };
    } catch (e) {
      console.error(e);
      return {
        success: false,
        message: 'An unknown error occurred while trying to update the answer template.',
      };
    }
  },
  /**
   * Updates an existing answer template to a new state.
   * Only certain fields can be changed
   * @param {string} botId The ID of the bot that has the template. Can be live or staging bot ID
   * @param {string} templateId The ID of the template to delete
   * @returns {Promise<{success: false; message: string} | {success: true}>} If `success: false`, a `message` contains the error and `template` is not defined.
   * If success, `message` is not set.
   */
  async deleteAnswerTemplate(botId: string, templateId: string) {
    try {
      const r = await ApiService.delete(`/schaltzentrale/rag/v3/bot/${botId}/templates/${templateId}`);

      if (r.data.message) {
        return {
          success: false,
          message: r.data.message,
        };
      }

      return {
        success: true,
      };
    } catch (e) {
      console.error(e);
      return {
        success: false,
        message: 'An unknown error occurred while trying to update the answer template.',
      };
    }
  },

  /**
   * Publish the templates in staging to live. Optionally limit to only specific channels/intents.
   * ⚠️ This will wipe all (or limited by your filters) existing Live templates and re-build them from staging bot.
   * @param {string} botId Staging or Live bot ID. Does not matter at all which you use.
   * @param {string[]} [channelIds=[]] Note: Does NOT use `null` for the default channel.
   * Limit templates being wiped and published to those that are used in the given channel.
   * If omitted, it will not filter based on this.
   * @param {string[]} [intents=[]] The intent technical names.
   * Limit templates being wiped and published to those that are used in the given intent.
   * If omitted, it will not filter based on this.
   * @returns {Promise<{success: false; message: string} | {success: true; templates: {live: AnswerTemplateFullDocument[], staging: AnswerTemplateFullDocument[]}}>} If `success: false`, a `message` contains the error and `templates` is not defined.
   * If success, `message` is not set and `templates` contains the templates that are now in live an staging, based on your filters.
   */
  async publishAnswerTemplates(botId: string, channelIds: string[] = [], intents: string[] = []) {
    const query = new URLSearchParams({});

    for (const id of channelIds) query.append(`channelIds`, id);
    for (const intent of intents) query.append(`intents`, intent);

    try {
      const r = await ApiService.post(`/schaltzentrale/rag/v3/bot/${botId}/templates/publish?${query.toString()}`);

      if (r.data.message) {
        return {
          success: false,
          message: r.data.message,
        };
      }

      return {
        success: true,
        templates: r.data,
      };
    } catch (e) {
      console.error(e);
      return {
        success: false,
        message: 'An unknown error occurred while trying to retrieve answer templates.',
      };
    }
  },

  /**
   * Get a preview of a CSV resource by asking the CSV agent endpoint.
   * @param {string} botId
   * @param {string} url
   * @param {string} [question] Optional question, defaults to 'Wie sieht die Tabelle aus?'
   * @returns {Promise<{rowCount: number, sampleRow: string}|{error: string}>}
   */
  async getCSVPreview(botId: string, url: string, question = 'Wie sieht die Tabelle aus?') {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/agents/csv/ask/${botId}`,
        { url, question },
      );
      if (!response.data || response.data.status !== 'success') {
        return {
          success: false,
          error: response.data?.message || 'Error fetching CSV preview.',
        };
      }

      return { success: true, data: response.data };
    } catch (error) {
      const axiosError = error as AxiosErrorLike;
      return {
        success: false,
        error: axiosError.response?.data?.message || axiosError.message || 'Error fetching CSV preview.',
      };
    }
  },
	/**
	 * Retrieves RAG Resource Markdown for provided 'bot' and 'resourceId'
	 * @param {{
	 *  botId: string,
	 *  resourceId: string,
	 * }} params
	 * @returns {Promise<{ md?: string }[]>}
	 */
	async getRAGResourceMarkdown({ botId, resourceId }: { botId: string; resourceId: string }) {
		try {
			const url = rag(botId, `/ressource/${resourceId}/preview`);

			const data = await ApiService.get(url);
			return data.data?.result;
		} catch(e) {
			console.error(e);
			return null;
		}
	},
  /**
   * Updates a resource abstract.
   * @param {{
   *  botId: string,
   *  resourceDocId: string,
   *  title?: string,
   *  abstractText?: string,
   *  regenerate?: boolean,
   *  embeddingName?: string,
   *  protectedAbstract?: boolean,
   * }} params
   * @returns {Promise<any>}
   */
  async updateResourceAbstract({
    botId,
    resourceDocId,
    title,
    abstractText,
    regenerate,
    embeddingName,
    protectedAbstract,
  }: ResourceAbstractParams) {
    try {
      const url = rag(botId, `/ressource/${resourceDocId}/abstract`);
      const payload: Record<string, any> = { abstractText, regenerate, protectedAbstract };
      if (title) payload.title = title;
      if (embeddingName) payload.embeddingName = embeddingName;
      const result = await ApiService.put(url, payload);
      return result.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * Returns the RAG protocol for the most recent RAG call in a conversation, enriched
   * with an `unknownReason` field explaining why the bot produced no useful answer.
   * @param {string} botId
   * @param {string} conversationId
   * @returns {Promise<{ragProtocol: Object|null, unknownReason: {reason: string, hint: string}|null}|null>}
   */
  async getConversationRagUnknownReason(botId: string, conversationId: string) {
    try {
    const r = await ApiService.get(
        `/schaltzentrale/rag/bot/${botId}/conversation/${conversationId}/rag-unknown-reason`
      );
      return r.data?.result ?? null;
    } catch (e) {
      console.error(e);
      return {
        ragProtocol: null,
        unknownReason: { reason: 'RAG_SERVICE_ERROR', hint: 'An error occurred while fetching the RAG unknown reason.' },
      };
    }
  },
  /**
   * Loads all resources for a given bot, and possibly conditions.
   * @param {string} botId The ID of the live bot, regardless of whether you want to find resources in staging or live
   * @param {?LoadRagResourcesFilters} [filters]
   * @param {?LoadRagResourcesOptions} [options={}]
   * @returns {Promise<{resources: object[]}>}
   */
  async getResources(botId: string, filters: LoadRagResourcesFilters = {}, options: LoadRagResourcesOptions = {}) {
    const query = new URLSearchParams({});
    for (const resourceId of filters.resourceIds ?? []) {
      query.append(`resourceIds`, resourceId);
    }
    for (const status of filters.statuses ?? []) {
      query.append(`statuses`, status);
    }
    for (const channelId of filters.channelIds ?? []) {
      query.append(`channelIds`, channelId);
    }
    if (options.withConnections) query.append('withConnections', 'true');
    if (options.withContent) query.append('withContent', 'true');
    if (options.withDeleted) query.append('withDeleted', 'true');

    const response = await ApiService.get(`/schaltzentrale/rag/v3/bot/${botId}/resources?${query.toString()}`);
    return response.data;
  },
};

export default RAGService;
export { RAGService };


/////////////////////////// UTILITY TYPES & ENUMS //////////////////////////////
/**
 * Types of statuses a resource can have.<br/>
 * <h3>NOTE:</h3> The "draft" status exist only in client-side,
 * it does not exist in the backend!
 * @typedef {'draft'|'active'|'active_failed_update'|'failed'|'queued'|'uploading'|'scraping'|'indexing'|'failed_scrape'|'failed_indexing'|'failed_quota_reached'|'failed_too_large'|'deleted'} RAGStatus
 */
/**
 * @typedef {'PDF'|'WEBPAGE'|'MARKDOWN'|'QNA'|'TABLE'|'EXTERNAL'} RAGResourceType
 */
/**
 * Custom hashmap you can attach to a resource
 * @typedef {Record<string, string>} RAGArbitraryMetaData
 */
/**
 * @typedef {"public"|"private"} RAGPrivacyStatus
 */
/**
 * @typedef {RagPDF | RagWEBPAGE | RagMARKDOWN | RagQNA} RagANY
 */
/**
 * @typedef {'text'|'buttons'|'cards'} AnswerTemplateType
 */
/**
 * @typedef {'web_url'} TemplateButtonType
 */

////////////////////////////////// QUERYING ////////////////////////////////////
/**
 * @typedef {Object} RAGQueryOptions
 * @property {number} [topK] Limit results to the top matches
 * @property {Record<string, string>} [filter] Limit results to match records with given metadata
 */
/**
 * @typedef {Object & RAGArbitraryMetaData} RAGDocumentMetadata
 * @property {string} [page_label] The page this item is on, e.g. "1" or "2"
 * @property {string} intent The intent this document is linked to
 * @property {string} botId The bot this document is linked to
 * @property {string} url The URL to the resource (either website URL or URL to the PDF)
 * @property {RAGResourceType} type The type of resource, either "WEBSITE" or "PDF"
 * @property {?string} [sourceDocument] The name you provided if PDF, else the URL of the website
 * @property {string} createdAt The creation date as an ISO string
 * @property {string} ressourceId The resource ID this document belongs to
 * @property {string} _node_content The actual content of this document, e.g. some content from the PDF if a PDF Resource
 * @property {"TextNode"} _node_typ The type of node content. For now, only "TextNode", until more features are added
 * @property {string} document_id A unique ID of this document
 * @property {string} doc_id The ID of this document
 * @property {string} ref_doc_id Reference document ID
 */
/**
 * @typedef {Object} RAGDocument
 * @property {number} id The ID of this document
 * @property {string} text The content of this document, e.g. the content of the first page in a PDF
 * @property {RAGDocumentMetadata & RAGArbitraryMetaData} metadata Metadata about this document.
 * This object also merges in your {@link RAGArbitraryMetaData} object, so you can use that to store additional metadata, directly inside this object, alongside other data.
 */
/**
 * @typedef {Object} RagIntent
 * @property {string} intent The technical name of the intent
 * @property {string[]} stagingChannels The channels this intent is used in staging. May contain the string literal "null" to represent the default channel.
 * @property {string[]} liveChannels The channels this intent is used in production. May contain the string literal "null" to represent the default channel.
 */
/**
 * Object is unknown.
 * @typedef {Object} RAGResourceErrorMessage
 */
/**
 * @typedef {Object} RAGResourceUpdate
 * @property {Date|string} updatedAt The last known update of the status/state of this resource.
 * @property {RAGResourceUpdateError} error Contains details about the error. Could be related to the initial creation too, not just re-scraping
 */
/**
 * @typedef {Object} RAGResourceUpdateError
 * @property {string} date The date of the error, ISO string.
 * @property {string} message A human-crafted descriptive error of what went wrong.
 * @property {RAGResourceUpdateErrorPayload} payload Contains additional details related to the error.
 */
/**
 * @typedef {Object} RAGResourceUpdateErrorPayload
 * @property {string} stack The error stacktrace
 * @property {string} message The error message itself
 */
/**
 * @typedef {Object} RAGUpdateData
 * @property {string|Date} [updatedAt] The date the resource was last re-fetched from the URL and updated
 * @property {boolean} [active] Whether the auto-update is active or not
 * @property {number} [period] The day interval to update the resource at
 */
/**
 * Options for web scraping.
 * There may be more undocumented properties of this object that are vendor-specific (the scraping service), but still entirely valid.
 * The same goes for new options added to the API, but no changes were needed in the frontend.
 * @typedef {Object} RAGResourceScrapeOptions
 * @property {?Record<string, string>} [headers] HTTP Header key (lowercased) and values, e.g. `{authorization: 'basic asdasdasd'}`.
 * @property {?number} [timeout] The timeout in milliseconds for the scraping operation
 * @property {?any[]} [actions] An array of arbitrary action objects.
 * @property {?boolean} [onlyMainContent] When scraping, focus only on retrieving the main content of the webpage
 */
/**
 * Additional configuration for this resource.
 * Can mostly be found in some `WEBPAGE` resources.
 * @typedef {Object} RAGResourceOptions
 * @property {?RAGResourceScrapeOptions} [webScraping] For `WEBPAGE`: Options related to Web Scraping
 */
/**
 * @typedef {Object} RAGResource
 * @property {string} _id Database object ID. Object ID.
 * @property {string} ressourceId The main ID of this resource, linking all docs together
 * @property {string[]} docIds List of document IDs. UUID.
 * @property {string} botId The bot this resource is linked to
 * @property {?string} [intent] DEPRECATED: The intent this resource is linked to. From before intents were re-usable in other intents.
 * @property {RAGResourceType} type The type of resource, either "WEBSITE" or "PDF"
 * @property {string} url The URL to the resource (either website URL or URL to the PDF)
 * @property {string} date The date this document was created
 * @property {string|Record<string, string>} metaData A string or an object containing some arbitrary metadata, which can be used for categorizing and lookups
 * @property {number} numDocs The number of documents this resource contains. Might be higher than `docIds.length` by 1
 * @property {string|null} [comment] An optional comment that was attached
 * @property {string|null} [description] Describes this resource
 * @property {?RAGDocument[]} [docs] All the documents of this resource
 * @property {?string} [sourceDocument] The name of the source document if PDF, else the URL of the website
 * @property {RAGStatus} status The status of this resource, e.g. "active"
 * @property {RagIntent[]} [intents] The intents this resource is used in
 * @property {RAGResourceErrorMessage[]} [errorMessages] Any error messages that occurred during creation
 * @property {RAGResourceUpdate} [update] The result of some status updates. Not specifically related to re-scraping, but also general status changes
 * @property {{markdown: string}} [content] The content of the resource, if it is a markdown article
 * @property {?RAGResourceOptions} [options] Additional configuration for this resource.
 * Can mostly be found in some `WEBPAGE` resources.
 * @property {RAGPrivacyStatus} privacy If the resource is viewable by the public or not
 */
/**
 * @typedef {Object} RAGResourceOverview
 * @property {string} _id Database object ID. Object ID.
 * @property {string[]} docIds List of document IDs. UUID.
 * @property {string} botId The bot this resource is linked to
 * @property {?number|null} [characterCount] The count of characters the content of the resource is.
 * Might be undefined, null, or a number. It can be null temporarily while the resource is being processed.
 * @property {?string} [intent] DEPRECATED: The intent this resource is linked to. From old when intents were not re-usable in other intents.
 * @property {null|Record<string, string>} metaData Null, a string or an object containing some arbitrary metadata, which can be used for categorizing and lookups
 * @property {RAGStatus} status The status of this resource, e.g. "active"
 * @property {null|string} [comment] An optional comment that was attached
 * @property {null|string} [description] Describes this resource
 * @property {RAGPrivacyStatus} privacy If the resource is viewable by the public or not
 * @property {number} __v Mongoose versioning key
 * @property {string} date The date this document was created
 * @property {number} numDocs How many documents (not to be confused with pages in the file) this resource contains
 * @property {string} ressourceId The main ID of this resource, linking all docs together
 * @property {RagIntent[]} [intents] The intents this resource is used in. May be unset if not used anywhere.
 * @property {RAGUpdateData} [update] A resource's auto-update configuration and when it was last fetched
 * @property {RAGResourceType} [type] The type of resource, e.g. "WEBSITE" or "PDF"
 */
/**
 * @typedef {Object} RAGQueryResponse
 * @property {"ok"|"error"} status Should be "ok" if all went well
 */
/**
 * @typedef {Object & RAGQueryResponse} RAGDeleteResponse
 * @property {{status: string, num_docs_deleted: number}} result The result of the deletion
 */
/**
 * @typedef {Object} RAGSourceUsed
 * @property {string} source The name of the source used, e.g. the filename if you used PDF
 * @property {RAGResourceType} type The type of source used, e.g. "PDF" or "WEBSITE"
 * @property {?string} [page] The page the source was from if PDF
 */
/**
 * @typedef {Object} RAGAnswer
 * @property {string} answer The actual text answer given by the AI
 * @property {RAGSourceUsed[]} sources The sources the AI used to arrive at this result
 * @property {RAGProtocol} ragProtocol The `ragProtocol` object for the answer generation
 */


//////////////////////// RESOURCE CREATION & RESPONSE //////////////////////////
/**
 * Payload for creating a new resource from a PDF file that is hosted at a URL
 * @typedef {Object} RagPDF
 * @property {'PDF'} type Always "PDF" for PDF resources
 * @property {string} url A HTTP(s) URL to where the PDF file is hosted. Usually our media store.
 * @property {string} fileName The name of the file (used in UI), including .pdf extension
 * @property {?RAGArbitraryMetaData} [metaData] An object containing some arbitrary metadata, which can be used for categorizing and lookups
 * @property {?string} [description] A description of the PDF file, what it is for and such. Shown in the UI
 * @property {?string} [comment] A comment about the PDF file
 */
/**
 * Payload for creating a new resource from a PDF file that is hosted at a URL
 * @typedef {Object} RagTable
 * @property {'TABLE'} type Always "PDF" for PDF resources
 * @property {string} url A HTTP(s) URL to where the PDF file is hosted. Usually our media store.
 * @property {string} fileName The name of the file (used in UI), including .json and .csv extensions
 * @property {?RAGArbitraryMetaData} [metaData] An object containing some arbitrary metadata, which can be used for categorizing and lookups
 * @property {?string} [description] A description of the PDF file, what it is for and such. Shown in the UI
 * @property {?string} [comment] A comment about the PDF file
 */
/**
 * Payload for creating a new website resource
 * @typedef {Object} RagWEBPAGE
 * @property {'WEBPAGE'} type Always "WEBPAGE" for website resources
 * @property {string} url A HTTP(s) URL to where the website is hosted
 * @property {?RAGArbitraryMetaData} [metaData] An object containing some arbitrary metadata, which can be used for categorizing and lookups
 * @property {?string} [description] A description of the website, what it is for and such. Shown in the UI
 * @property {?string} [comment] A comment about the website
 */
/**
 * Payload for creating a new Markdown Article
 * @typedef {Object} RagMARKDOWN
 * @property {'MARKDOWN'} type Always "MARKDOWN" for markdown articles
 * @property {{markdown: string}} content An object containing a markdown property, which contains the actual markdown
 * @property {{title: string} & ?RAGArbitraryMetaData} metaData A `title` property holding the title, and any other arbitrary data you want
 * @property {?string} [description] A description of the website, what it is for and such. Shown in the UI
 * @property {?string} [comment] A comment about the website
 */
/**
 * Payload for creating a new Question & Answer resource
 * @typedef {Object} RagQNA
 * @property {'QNA'} type Always "QNA" for Question & Answer articles
 * @property {string} name An article name, purely for UI purposes
 * @property {{question: string, answer: string}} content Question and Answer pair. Question being plaintext, Answer being Markdown.
 * @property {?RAGArbitraryMetaData} [metaData] An object containing some arbitrary metadata, which can be used for categorizing and lookups
 * @property {?string} [description] A description of the website, what it is for and such. Shown in the UI
 * @property {?string} [comment] A comment about the website
 */

/**
 * Fields that are always returned when you create a resource of any type,
 * that is not already defined in the specific RagX typedefs.
 * @typedef {Object} __PostRAGResourceResponseExtraFields
 * @property {string} _id The MongoDB ID
 * @property {string[]} docIds A list of other document's IDs that will contain the actual content,
 * broken up into smaller parts. Gets populated as the resource is processed, so usually empty initially.
 * @property {string[]} errorMessages Any error messages that occurred during creation
 * @property {string} date The date of creation as an ISO string
 * @property {RAGStatus} status The status of the resource, e.g. "uploading"
 * @property {number} __v Mongoose versioning key
 * @property {?string} [ressourceId] The ID to the resource, linking all docs together
 */
/**
 * @template {RagPDF | RagWEBPAGE | RagMARKDOWN | RagQNA | RagTable} ResourcePayload
 * @typedef {ResourcePayload & __PostRAGResourceResponseExtraFields} RemoteRAGResource
 */

/**
 * @typedef {Object} ActionParameterDescription
 * @property {string} name - The parameter name
 * @property {string} type - The parameter type (e.g., 'string')
 * @property {string} paramDescription - Description of what this parameter is for
 */
/**
 * @typedef {string|'webhook'|'followup'|'webSearchFallback'} RAGActionType
 */
/**
 * A RAG Action object.
 * These are found inside an intent.
 * @typedef {Object} RAGAction
 * @property {RAGActionType} [actionType] The type of action. If `undefined` (old): it is a `webhook` if `webhookId` is present, else it is a `followup` type
 * @property {?string} [id] A unique ID of this action.
 * Created by backend and provided in response when you create a new one.
 * @property {string} [webhookId] - For webhook: ID of the webhook to use, since we only support webhooks as tools for now
 * @property {string} [url] - For webSearchFallback: The site we will limit search results to
 * @property {string} name - Display name for the action
 * @property {string} description - Description of action. Ideally user-written so the LLM knows what this tool does
 * @property {string} instruction_before - What LLM should say before the result is ready (e.g., "Let me check that for you")
 * @property {string} instruction_after - What LLM should say after the result is ready (e.g., "Here is what I found")
 * @property {ActionParameterDescription[]} parameters - Parameters this action accepts
 */
/**
 * The {@link RAGAction}, but with additional properties needed for interacting with the API
 * @typedef {RAGAction} RAGActionPayload
 * @property {string} webhookId - ID of the webhook to use, since we only support webhooks as tools for now
 * @property {string} channelId - Current channel ID. Not 'null' if default; the actual channel ID
 */

/**
 * The `ragActions` property of a RAGTopic intent
 * @typedef {Object} IntentRagActions
 * @property {?Object.<string, RAGAction[]>} [staging] Object key is channel ID
 * @property {?Object.<string, RAGAction[]>} [live] Object key is channel ID
 */

/**
 * @typedef {Object} RagSearchResult
 * @property {'ok'|'error'|string} status
 * @property {(Object & {rag: (Object & RAGResource & {linkedFaqs: Object[]})})[]} [result]
 */

/**
 * A RAG Followup Action object.
 * These are found inside an intent.
 * @typedef {Object} RAGFollowupAction
 * @property {string} [description] - Description of what this followup action does
 * @property {string} [instruction_before] - What LLM should say before the followup
 * @property {any[]} options - List of followup options to present to the user
 */

/**
 * The {@link RAGFollowupAction}, but with additional property needed for interacting with the API
 * @typedef {Object} RAGFollowupActionPayload
 * @extends {RAGFollowupAction}
 * @property {string} channelId - Current channel ID. Not 'null' if default; the actual channel ID
 */

/**
 * Output gate configuration for a RAG agent/intent channel.
 * @typedef {Object} RAGOutputGate
 * @property {boolean} [enabled]
 * @property {string} [type] Identifies the scenario template (e.g. 'product_search_narrowing', 'knowledge_disambiguation')
 * @property {boolean} [requires_grounded_action_before_refinement]
 * @property {number} [max_answer_candidates]
 * @property {number} [narrowing_turn_budget]
 */

/**
 * The {@link RAGOutputGate}, but with additional properties needed for interacting with the API
 * @typedef {Object} RAGOutputGatePayload
 * @property {string} channelId - Current channel ID
 * @property {RAGOutputGate|null} output_gate - Pass `null` to remove the whole gate object
 */

/**
 * @typedef {Object} ButtonAnswer
 * @property {TemplateButtonType} type The type of the button (e.g., 'postback', 'url').
 * @property {string} title The title of the button.
 * @property {string} url The URL the button points to
 */
/**
 * @typedef {Object} CardAnswer
 * @property {string} image_url The URL of the image for the card.
 * @property {string} title The title of the card.
 * @property {string} subtitle The subtitle of the card.
 * @property {ButtonAnswer[]} buttons An array of buttons associated with the card.
 */
/**
 * @typedef {Object} AnswerTemplateAnswer
 * @property {string} text The text answer bot will provide.
 * @property {?ButtonAnswer[]} [buttons] An array of buttons associated with the answer.
 * @property {?CardAnswer[]} [cards] An array of card answers associated with the answer.
 */
/**
 * @typedef {Object} AnswerTemplate
 * @property {string} name A display name of the answer template.
 * @property {AnswerTemplateType} type The type of answer template this is
 * @property {string} instruction The instruction for the answer template.
 * @property {AnswerTemplateAnswer} answer The answer object containing text, buttons, and cards.
 */
/**
 * @typedef {AnswerTemplate} AnswerTemplateFullDocument
 * @property {import('mongoose').SchemaType.ObjectId} [_id] MongoDB Object ID for this template
 * @property {string} botId The ID of the bot this template belongs to
 * @property {string} intent The intent this template is associated with
 * @property {string} channelId The channel ID this template is associated with
 * @property {?import('mongoose').SchemaType.ObjectId} [basedOn] For published templates in live: Tells you which staging template ID this was originally based on
 * @property {Date} [createdAt] The date when the answer template was created.
 * @property {Date} [updatedAt] The date when the answer template was last updated.
 */
/**
 * @typedef {Object} AvailableTemplate
 * @property {string} id Identifier for type of template
 * @property {AnswerTemplate} template The template object containing the actual template structure
 */
/**
 * @typedef {Object} RAGCreationResult
 * @property {"ok"|"error"} status Either "ok" or "error"
 * @property {string} result A display message describing what happened.
 */
/**
 * @typedef {Object} RAGAnswerSourceContent
 * @property {string} ressourceId The RAG Resource ID (not ObjectID)
 * @property {string} text The actual text content
 * @property {RAGResourceType} type The type of resource this is
 * @property {?string} [url] The URL of the source (WEBPAGE)
 */
/**
 * @typedef {Object} RAGProtocol
 * @property {RAGResource[]} simDocs Similarity search results
 * @property {RAGAnswerSourceContent[]} usedKnowledge Information the LLM received as likely relevant for answering (used in prompt)
 * @property {RAGAnswerSourceContent[]} sources Which of the sources from `usedKnowledge` the LLM ended up using for the answer (used for answer)
 */


// ////////////////////////////////////////////////////////////////////////////////
// /**
//  * A non-exposed generic function for creating resources.
//  * Used {@link addPDFResource}, {@link addWebsiteResource},
//  * {@link addMarkdownResource}, or {@link addQNAResource} instead of this directly.
//  * @function
//  * @template {RagPDF | RagWEBPAGE | RagMARKDOWN | RagQNA | RagTable} ResourcePayload
//  * @param {string} botId
//  * @param {ResourcePayload} resource
//  * @throws {Error} If it fails to call the API
//  * @returns {Promise<null | RemoteRAGResource<ResourcePayload>>} `null` if failed to read the properties
//  */
// async function createResource(botId, resource) {
//   const r = await ApiService.post(rag(botId, `/resources`, true), resource);
//   return r.data?.ressource ?? null;
// }

// /**
//  * PUT Overwrite a {@link addPDFResource}, {@link addWebsiteResource},
//  * {@link addMarkdownResource}, or {@link addQNAResource} with the new content.
//  * @function
//  * @template {RagPDF | RagWEBPAGE | RagMARKDOWN | RagQNA | RagTable} ResourcePayload
//  * @param {string} botId
//  * @param {string} docId The resource Document ID (_id)
//  * @param {ResourcePayload} resource
//  * @throws {Error} If it fails to call the API
//  * @returns {Promise<null | RemoteRAGResource<ResourcePayload>>}
//  */
// async function updateResource(botId, docId, resource) {
//   const r = await ApiService.put(rag(botId, `/resources/update/${docId}`, true), resource);
//   return r.data?.ressource ?? null;
// }
